import PageHero from "@/components/PageHero";
import { FadeUp, StaggerChildren, StaggerItem } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";

/**
 * The seven distinctions.
 *
 * Each closes on a single line that states the point plainly. Those lines carry
 * the argument of the page, so they are set apart from the explanation rather
 * than buried at the end of a paragraph.
 */
const points = [
  {
    k: "Independent Advice",
    lead: "Advice without a product to sell.",
    body: [
      "PIRAC is not driven by listings, sales targets or a portfolio of properties that need to be moved.",
      "Our recommendations are based on the client's objectives, circumstances and the merits of the opportunity. If a property does not make sense, we are prepared to say so.",
    ],
    close: "The objective is not to complete a transaction. It is to make the right decision.",
  },
  {
    k: "Collective Expertise",
    lead: "More than one perspective. One considered view.",
    body: [
      "Every engagement benefits from the collective experience of our three advisors.",
      "Different perspectives allow assumptions to be challenged, opportunities to be examined from multiple angles and potential risks to be identified earlier. Clients benefit from the depth of a team rather than relying solely on the judgement of one individual.",
    ],
    close: "Three perspectives create a more complete view.",
  },
  {
    k: "Research-Driven Decisions",
    lead: "Evidence before opinion.",
    body: [
      "Our recommendations are supported by structured research, market intelligence, transaction evidence, financial feasibility and scenario analysis.",
      "We examine the data behind the opportunity and distinguish between facts, assumptions and professional judgement. Where the future is uncertain, we consider different scenarios rather than presenting forecasts as certainty.",
    ],
    close: "We do not simply tell clients what we think. We show them why.",
  },
  {
    k: "Deep Singapore Market Knowledge",
    lead: "Experience that goes beyond today's market.",
    body: [
      "Singapore property requires an understanding of more than prices and locations. It requires knowledge of market cycles, policy changes, financing conditions, regulations, supply dynamics and the way different property sectors respond to them.",
      "Our collective experience spans HDB, private residential, commercial and industrial property, giving us a broad perspective across Singapore's real estate landscape.",
    ],
    close: "We bring market memory to today's decisions.",
  },
  {
    k: "Personalised Strategies",
    lead: "No templates. No one-size-fits-all recommendations.",
    body: [
      "Every client has different objectives, financial circumstances, financing considerations, CPF position, risk tolerance and investment horizon.",
      "We therefore develop strategies around the individual rather than fitting the individual into a predefined strategy. The same property can represent an excellent opportunity for one client and a poor decision for another.",
    ],
    close: "The right property is the one that is right for you.",
  },
  {
    k: "Transparent Communication",
    lead: "Clear assumptions. Clear evidence. Clear trade-offs.",
    body: [
      "Good advice should be understandable.",
      "We aim to make the reasoning behind our recommendations transparent, including relevant data, assumptions, financial considerations, risks and trade-offs. Where there are uncertainties, we identify them. Where the numbers depend on assumptions, we make those assumptions visible.",
    ],
    close: "No hidden calculations. No unexplained conclusions.",
  },
  {
    k: "Long-Term Relationships",
    lead: "The relationship should outlast the transaction.",
    body: [
      "A property decision may take months to make, but its consequences can last for decades.",
      "PIRAC is structured around long-term advisory relationships rather than one-off transactions. As circumstances, markets and objectives change, we can continue to review the strategy and help clients make informed decisions at different stages of their property journey.",
      "Because the real measure of a property decision is not simply what happens on completion day — but where that decision leaves the client years later.",
    ],
    close: "We think beyond the deal. We think about the decade that follows.",
  },
];

const compareLeft = [
  "Begins with available properties",
  "Focuses mainly on completing a transaction",
  "May examine fewer alternatives",
  "Often short-term",
];
const compareRight = [
  "Begins with client objectives",
  "Uses market and financial analysis",
  "Compares opportunities and risks",
  "Applies a structured methodology",
  "Builds a longer-term strategy",
];

export default function WhyPirac() {
  return (
    <>
      <SeoHead pageKey="why_pirac" />
      <PageHero
        eyebrow="Why PIRAC"
        chapter="Chapter — Distinction"
        lines={["Independent,", "by Design."]}
        blurb="A different starting point produces a different recommendation."
        image="/assets/hero-why-pirac"
        imageAlt="A desk plaque reading Independent Advice. Better Decisions., beside a PIRAC property analysis report and a stack of research volumes, overlooking Marina Bay"
      />

      <section className="bg-[#060f1e] pt-24 lg:pt-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <FadeUp>
            <div className="max-w-3xl space-y-6 text-[#d8d8d8] leading-relaxed text-lg font-light">
              <p>
                Traditional property models often begin with the transaction — what can be bought, sold
                or marketed. PIRAC begins somewhere else: with the client, the objective and the decision
                that needs to be made.
              </p>
              <p className="text-[#f7f5f0]">We believe this distinction matters.</p>
              <p>
                When advice is independent of a transaction, the recommendation does not have to lead to
                a purchase or sale. Sometimes the right decision is to proceed. Sometimes it is to wait,
                negotiate differently, restructure an existing portfolio or walk away.
              </p>
              <p>
                Our approach is designed to give clients the freedom to make those decisions with greater
                clarity and confidence.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#060f1e] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <StaggerChildren className="divide-y divide-[#1a2940] border-y border-[#1a2940]">
            {points.map((p, i) => (
              <StaggerItem key={p.k}>
                <div className="grid grid-cols-12 gap-6 py-10 lg:py-12 group">
                  <div className="col-span-12 lg:col-span-5">
                    <div className="flex items-baseline gap-4">
                      <span className="text-xs text-[#cca24d]/60 tnum">{String(i + 1).padStart(2, "0")}</span>
                      <h2 className="font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight group-hover:text-[#cca24d] transition-colors">
                        {p.k}
                      </h2>
                    </div>
                    <p className="mt-3 lg:ml-9 text-sm text-[#cca24d]/80 italic">{p.lead}</p>
                  </div>
                  <div className="col-span-12 lg:col-span-7 space-y-4">
                    {p.body.map((paragraph) => (
                      <p key={paragraph.slice(0, 40)} className="text-[#9ca3af] leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                    <p className="pt-3 text-[#f7f5f0]">{p.close}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>

          {/* Closing argument — the page resolves to a single claim. */}
          <FadeUp delay={0.1}>
            <div className="mt-16 border border-[#cca24d]/30 bg-[#0c1728] p-8 lg:p-14">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#f7f5f0] font-light tracking-tight">
                Why <em className="text-[#cca24d] not-italic font-normal italic">PIRAC?</em>
              </h2>
              <div className="mt-8 max-w-4xl space-y-5 text-[#d8d8d8] leading-relaxed text-lg font-light">
                <p>
                  Because significant property decisions deserve more than a salesperson's opinion.
                </p>
                <p>
                  They deserve independent advice, collective experience, rigorous research, transparent
                  analysis and a strategy built around the individual.
                </p>
                <p>That is the difference between intermediation and advisory.</p>
              </div>
              <div className="mt-10 pt-8 border-t border-[#1a2940]">
                <p className="font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight">
                  We don't start with the property.
                  <br />
                  <span className="text-[#cca24d]">We start with you.</span>
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#0c1728] border-y border-[#1a2940] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <FadeUp><span className="overline">Approach Comparison</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight leading-[1.05]">
                The same market. A different starting point.
              </h2>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FadeUp>
              <div className="h-full p-10 lg:p-12 border border-[#1a2940] bg-[#060f1e]">
                <span className="overline text-[#9ca3af]">Transaction-led</span>
                <h3 className="mt-6 font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight mb-8">Starts with the property.</h3>
                <ul className="space-y-4">
                  {compareLeft.map((l) => (
                    <li key={l} className="flex gap-3 text-[#9ca3af]">
                      <span className="mt-2 h-px w-4 bg-[#1a2940] flex-shrink-0" />
                      <span className="text-sm leading-relaxed">{l}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="h-full p-10 lg:p-12 border border-[#cca24d]/60 bg-[#060f1e] relative">
                <span className="overline">PIRAC Advisory</span>
                <h3 className="mt-6 font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight mb-8">Starts with the client.</h3>
                <ul className="space-y-4">
                  {compareRight.map((l) => (
                    <li key={l} className="flex gap-3 text-[#d8d8d8]">
                      <span className="mt-2 h-px w-4 bg-[#cca24d] flex-shrink-0" />
                      <span className="text-sm leading-relaxed">{l}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
